import JSZip from 'jszip';

// Builds a minimal, valid .docx (Word Open XML) file client-side from plain
// text, so a user-reviewed/edited text (e.g. after converting a legacy-font
// Case Diary upload to Unicode) can be re-packaged and uploaded as a Word
// file instead of the original file the user selected. Deliberately plain -
// one paragraph per line, no styling/headers - this only needs to carry text
// content through an API that expects a .docx upload.

const CONTENT_TYPES_XML =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
  '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
  '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
  '<Default Extension="xml" ContentType="application/xml"/>' +
  '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>' +
  '</Types>';

const RELS_XML =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>' +
  '</Relationships>';

const escapeXml = (str) =>
  String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const buildDocumentXml = (text) => {
  const lines = String(text ?? '').replace(/\r\n/g, '\n').split('\n');

  const paragraphs = lines
    .map((line) => {
      // Empty lines still need a paragraph (with no run) to preserve blank lines.
      const run = line ? `<w:r><w:t xml:space="preserve">${escapeXml(line)}</w:t></w:r>` : '';
      return `<w:p>${run}</w:p>`;
    })
    .join('');

  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">' +
    `<w:body>${paragraphs}<w:sectPr/></w:body>` +
    '</w:document>'
  );
};

// Builds one <w:p> paragraph from a simple block description:
//   { text, bold, size (half-points), spacingBeforeTwips, spacingAfterTwips }
// Embedded newlines within `text` become <w:br/> line breaks inside the same
// paragraph. An empty/absent `text` with a large spacingAfterTwips produces a
// blank paragraph that reserves vertical space (e.g. room to write an answer
// by hand under an unanswered question).
const buildParagraphXml = ({ text = '', bold = false, size, spacingBeforeTwips, spacingAfterTwips, pageBreakBefore = false } = {}) => {
  const pPrParts = [];
  if (pageBreakBefore) pPrParts.push('<w:pageBreakBefore/>');

  const spacingAttrs = [];
  if (spacingBeforeTwips) spacingAttrs.push(`w:before="${spacingBeforeTwips}"`);
  if (spacingAfterTwips) spacingAttrs.push(`w:after="${spacingAfterTwips}"`);
  if (spacingAttrs.length) pPrParts.push(`<w:spacing ${spacingAttrs.join(' ')}/>`);

  const pPr = pPrParts.length ? `<w:pPr>${pPrParts.join('')}</w:pPr>` : '';

  if (!text) {
    return `<w:p>${pPr}</w:p>`;
  }

  const rPrParts = [];
  if (bold) rPrParts.push('<w:b/>');
  if (size) rPrParts.push(`<w:sz w:val="${size}"/>`);
  const rPr = rPrParts.length ? `<w:rPr>${rPrParts.join('')}</w:rPr>` : '';

  const runText = String(text)
    .split('\n')
    .map((line, i) => `${i > 0 ? '<w:br/>' : ''}<w:t xml:space="preserve">${escapeXml(line)}</w:t>`)
    .join('');

  return `<w:p>${pPr}<w:r>${rPr}${runText}</w:r></w:p>`;
};

// Returns a Blob containing a .docx built from an array of block
// descriptions (see buildParagraphXml above), one paragraph per block.
export const createStructuredDocxBlob = async (blocks) => {
  const zip = new JSZip();
  const paragraphs = (blocks || []).map(buildParagraphXml).join('');

  const documentXml =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">' +
    `<w:body>${paragraphs}<w:sectPr/></w:body>` +
    '</w:document>';

  zip.file('[Content_Types].xml', CONTENT_TYPES_XML);
  zip.folder('_rels').file('.rels', RELS_XML);
  zip.folder('word').file('document.xml', documentXml);

  return zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
};

// Wraps createStructuredDocxBlob's result in a File object with the given name.
export const createStructuredDocxFile = async (blocks, filename = 'document.docx') => {
  const blob = await createStructuredDocxBlob(blocks);
  return new File([blob], filename, {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
};

// Returns a Blob (mimetype set for .docx) containing the given text as a
// minimal Word document.
export const createDocxBlob = async (text) => {
  const zip = new JSZip();

  zip.file('[Content_Types].xml', CONTENT_TYPES_XML);
  zip.folder('_rels').file('.rels', RELS_XML);
  zip.folder('word').file('document.xml', buildDocumentXml(text));

  return zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
};

// Wraps createDocxBlob's result in a File object with the given name, ready
// to attach to a FormData upload in place of the originally-selected file.
export const createDocxFile = async (text, filename = 'document.docx') => {
  const blob = await createDocxBlob(text);
  return new File([blob], filename, {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
};
